import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Title } from '@meitu/saas-ui';
import type { Editor } from '@meitu/saas-editor-core';
import TagMenu, { TAG_TYPE } from '@/components/material-tag-menu';
import { useLocaleStore } from '@/i18n';
import { type ICategory, type IColorItem, type MaterialTopicItem, type TextCategoryItem } from '@/types';
import AllWatermarkList from './components/all-watermark-list/index';
import UploadImg from './components/upload-img/index';
import WatermarkDetailList from './components/watermark-detail-list/index';
import './index.less';
import { Wasm } from '@meitu/saas-editor-core/es/editor/extensions/wasm';
import { WatermarkLayer } from '@meitu/saas-editor-core/es/layers/watermark-layer';

const { BackTitle } = Title;

const useInitWatermark = (editor: Editor) => {
    const [initState, setInitState] = useState(false);

    const initWatermark = async () => {
        if (editor.getInstance(Wasm).getIsReady() && !editor.getInstance(WatermarkLayer).getInitState()) {
            editor.tip.showMask({
                loading: true,
                background: true,
            });

            await editor.getInstance(WatermarkLayer).initWatermarkImageKit();

            editor.tip.hideMask();
        }

        setInitState(editor.getInstance(WatermarkLayer).getInitState());
    };
    useEffect(() => {
        initWatermark();
        return editor.getInstance(Wasm).onWasmReady(initWatermark);
    }, []);

    return initState;
};

type WatermarkCollapseProps = {
    editor: Editor;
    CODE: any;
    jumpToLogin: () => Promise<string>;
    saveMyPicture: (data: any) => Promise<any>;
    fetchMaterialTopicList: (data: any) => Promise<any>;
    fetchSaveRecentUsed: (data: any) => Promise<any>;
    fetchMaterialsByCategory: (params: any) => Promise<any>;
    fetchNewMaterialList: (params: any) => Promise<any>;
    fetchRecentUsedList: (params: any) => Promise<any>;
    fetchImageMaterialTopiDetail: (params: any) => Promise<any>;
    // 从原来的 checkShowSvipCorner 改过来的
    showSvipCorner: boolean;
    // 最近使用
    setRecentWatermarkUsedList: (data: MaterialTopicItem) => void;
    recentWatermarkUsedList?: MaterialTopicItem;
    recordId?: number;
    colorsList: IColorItem[];
    getTagsList: () => Promise<TextCategoryItem[]>;
    // @FIXME 类型改成 IUserInfoParams
    userInfo: any;
    useListTrack: any;
    afterImageCreatedListener: (callback: Function) => {
        on: () => void;
        off: () => void;
    };
    onBack?: () => void;
};

const WatermarkCollapse = <MaterialTopicItem, WatermarkListItem>({
    CODE,
    editor,
    recordId,
    jumpToLogin,
    saveMyPicture,
    showSvipCorner,
    fetchMaterialTopicList,
    fetchSaveRecentUsed,
    recentWatermarkUsedList,
    setRecentWatermarkUsedList,
    colorsList,
    getTagsList,
    fetchMaterialsByCategory,
    fetchNewMaterialList,
    fetchRecentUsedList,
    fetchImageMaterialTopiDetail,
    userInfo,
    useListTrack,
    afterImageCreatedListener,
    onBack,
}: WatermarkCollapseProps) => {
    const { $t } = useLocaleStore();

    const [showDetails, setShowDetails] = useState<boolean>(false);
    const [category, setCategory] = useState<ICategory>();

    const [tagList, setTagList] = useState<TextCategoryItem[]>();
    const [tagChecked, setTagChecked] = useState<number | TAG_TYPE>(TAG_TYPE.all);
    const [currentTagType, setCurrentTagType] = useState<TAG_TYPE>(TAG_TYPE.all);
    const [color, setColor] = useState<IColorItem | null>(null);

    const setShowDetailsCollapse = (bool: boolean, name: string, id: string | number) => {
        setShowDetails(bool);
        setCategory({ name, id });
    };

    useEffect(() => {
        getTagsList().then(setTagList);
    }, []);

    // 切换标签
    const handleTagChange = useCallback((item: TextCategoryItem) => {
        if (typeof item.id === 'string') {
            if (item.id !== TAG_TYPE.more) {
                setCurrentTagType(item.id as TAG_TYPE);
                setTagChecked(item.id as TAG_TYPE);
                setCategory(undefined);
                if (item.id === TAG_TYPE.all) {
                    setShowDetails(false);
                }
            }
        } else {
            setCurrentTagType(TAG_TYPE.tag);
            setCategory(item);
            setTagChecked(item.id);
        }
        if (item.id !== TAG_TYPE.moreItem && item.id !== TAG_TYPE.more) {
            setColor(null);
        }
        window._stat_track?.('hbsj_subfunction_click', {
            first_func: 'sy',
            second_func: '',
            third_func: item.name,
        });
    }, []);

    const materialDetailListStyle = useMemo(() => {
        return {
            display: !color && ((currentTagType !== TAG_TYPE.all && !showDetails) || showDetails) ? 'flex' : 'none',
        };
    }, [color, currentTagType, showDetails]);

    // 创建新图片时重置
    useEffect(() => {
        const handleReset = () => {
            setShowDetails(false);
        };

        const listener = afterImageCreatedListener(handleReset);
        listener.on();

        return listener.off;
    }, []);

    const watermarkInitState = useInitWatermark(editor);
    if (!watermarkInitState) {
        return null;
    }
    return (
        <div className="c-watermark-collapse">
            {/* 进入专题列表后需要隐藏顶部返回标题 */}
            {onBack && !showDetails ? (
                <div className="c-element-collapse__title">
                    <BackTitle text={$t((v) => v.panel.watermarkPanel.watermark)} handleBackClick={onBack} />
                </div>
            ) : null}

            {!showDetails ? (
                <>
                    <UploadImg
                        editor={editor}
                        recordId={recordId}
                        jumpToLogin={jumpToLogin}
                        saveMyPicture={saveMyPicture}
                        userInfo={userInfo}
                    />
                    <TagMenu
                        tagsLimit={6}
                        tagChecked={tagChecked}
                        list={tagList}
                        tagChange={handleTagChange}
                        color={color}
                        setColor={setColor}
                        colorsList={colorsList}
                    />
                </>
            ) : null}
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                }}
            >
                <AllWatermarkList
                    editor={editor}
                    recordId={recordId}
                    userInfo={userInfo}
                    setShowDetailsCollapse={setShowDetailsCollapse}
                    fetchMaterialTopicList={fetchMaterialTopicList}
                    fetchSaveRecentUsed={fetchSaveRecentUsed}
                    setRecentWatermarkUsedList={setRecentWatermarkUsedList}
                    recentWatermarkUsedList={recentWatermarkUsedList}
                    style={{
                        display: currentTagType === TAG_TYPE.all && !showDetails ? 'block' : 'none',
                    }}
                    showSvipCorner={showSvipCorner}
                    useListTrack={useListTrack}
                />
                <WatermarkDetailList
                    editor={editor}
                    CODE={CODE}
                    recordId={recordId}
                    userInfo={userInfo}
                    showSvipCorner={showSvipCorner}
                    recentWatermarkUsedList={recentWatermarkUsedList}
                    setRecentWatermarkUsedList={setRecentWatermarkUsedList}
                    setShowDetailsCollapse={setShowDetailsCollapse}
                    categoryInfo={category}
                    isRecently={currentTagType === TAG_TYPE.recently}
                    isTagFilter={currentTagType === TAG_TYPE.tag}
                    style={materialDetailListStyle}
                    fetchSaveRecentUsed={fetchSaveRecentUsed}
                    fetchMaterialsByCategory={fetchMaterialsByCategory}
                    fetchImageMaterialTopiDetail={fetchImageMaterialTopiDetail}
                    fetchNewMaterialList={fetchNewMaterialList}
                    fetchRecentUsedList={fetchRecentUsedList}
                    useListTrack={useListTrack}
                />
            </div>
        </div>
    );
};
export default React.memo(WatermarkCollapse);
